import React, { useEffect, useRef, useState } from 'react';
import integrationsService from '../../services/integrationsService';

// SHARED RESYNC PROGRESS — the per-hotel progress list for a set of started resync runs. Given the runs a bulk resync
// started (by status, or from a selection), it polls each one and shows hotel · kind · record count · live processing
// status, until every run is terminal. Used by BulkStatusResyncModal and BulkSelectedResyncModal so both read the same.
//
// `runs`: [{ templateId, implementationId, hotelId, hotelname, type, runId, records, error? }] — the server's answer.
// Each run is the same OHIP Adapter resync Step Functions machine; getResyncStatus reads its OpenSearch state doc.

const TERMINAL = ['done', 'failed'];
const POLL_MS = 2500;
const statusDot = (status) => (status === 'done' ? '#16a34a' : status === 'failed' ? '#dc2626' : status === 'running' ? '#2563eb' : '#d1d5db');

// One line describing a run's state, from its OpenSearch document.
const runSummary = (run) => {
    if (!run) return 'starting…';
    if (run.status === 'queued') return 'queued — waiting for the run to start…';
    if (run.status === 'failed') return `failed${run.error ? ` — ${run.error}` : ''}`;
    const counts = (run.count_new || run.count_updated || run.count_unchanged)
        ? ` · ${run.count_new || 0} new, ${run.count_updated || 0} updated, ${run.count_unchanged || 0} unchanged` : '';
    if (run.status === 'done') return `done — ${run.sent || 0} sent${run.failed ? `, ${run.failed} failed` : ''}${counts}${run.note ? ` — ${run.note}` : ''}`;
    const step = (run.steps || []).find(s => s.status === 'running');
    const detail = step && (step.detail || step.label);
    return `${detail || 'running…'}${run.sent ? ` — ${run.sent}${run.total ? ` of ${run.total}` : ''} sent${counts}` : ''}`;
};

export default function ResyncProgress({ runs, noun = 'resyncs' }) {
    const [rows, setRows] = useState(() => (runs || []).map(r => ({ ...r, run: null })));
    const timer = useRef(null);

    useEffect(() => {
        const started = (runs || []).map(r => ({ ...r, run: null }));   // mutated in place across ticks (.run)
        setRows(started);
        let stopped = false;
        const stop = () => { stopped = true; if (timer.current) { clearInterval(timer.current); timer.current = null; } };
        const poll = async () => {
            await Promise.all(started.map(async (s) => {
                if (!s.runId || (s.run && TERMINAL.includes(s.run.status))) return;
                try {
                    const res = await integrationsService.getResyncStatus(s.templateId, s.implementationId, s.runId);
                    s.run = res.run || res;
                } catch (e) { /* a transient poll failure keeps the last state; the next tick retries */ }
            }));
            if (stopped) return;
            setRows([...started]);
            if (started.every(s => !s.runId || (s.run && TERMINAL.includes(s.run.status)))) stop();
        };
        if (started.some(s => s.runId)) { poll(); timer.current = setInterval(poll, POLL_MS); }
        return stop;
    }, [runs]);

    const done = rows.every(s => !s.runId || (s.run && TERMINAL.includes(s.run.status)));
    const startedCount = rows.filter(s => s.runId).length;
    const failedToStart = rows.filter(s => !s.runId).length;
    const totalRecords = rows.reduce((n, s) => n + (s.records || 0), 0);

    return (
        <>
            <p className="cm-message">
                {done
                    ? `The ${noun} have finished.`
                    : `The ${noun} are running. You can leave this open to watch them, or close it — they keep running.`}
                {' '}Started {startedCount} of {rows.length}{failedToStart ? `, ${failedToStart} could not start` : ''}.
                {totalRecords ? ` ${totalRecords} record${totalRecords === 1 ? '' : 's'} matched.` : ''}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 340, overflowY: 'auto',
                margin: '0.5rem 0 0.25rem' }}>
                {rows.map(s => (
                    <div key={s.runId || `${s.implementationId}-${s.type}`} style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem',
                        borderBottom: '1px solid #f1f5f9', padding: '0.35rem 0' }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, alignSelf: 'center',
                            background: s.error ? '#dc2626' : statusDot(s.run && s.run.status) }} />
                        <span style={{ fontWeight: 600, color: '#111827', minWidth: 150, flexShrink: 0 }}>{s.hotelname}</span>
                        <span style={{ fontSize: '0.8rem', color: '#6b7280', flexShrink: 0 }}>
                            {s.type}{s.records ? ` · ${s.records}` : ''}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: s.error ? '#dc2626' : '#6b7280', flex: 1, minWidth: 0 }}>
                            {s.error ? s.error : runSummary(s.run)}
                        </span>
                    </div>
                ))}
            </div>
        </>
    );
}
